/**
 * The painted sky window as a free camera sees it.
 *
 * Read unchanged, the painted window layout stands along meridians: its front becomes a straight
 * wall from horizon to zenith, and its clear and overcast parts become wedges that meet overhead.
 * A free camera therefore reads the window displaced in azimuth by a cloud-mass field (the STYLE
 * §4.2 aspect, 4 deg tall so that no run of the front stays vertical for 5 deg) whose 12 deg RMS
 * exceeds the width of the painted front (ten knots), so the bank's edge breaks into masses; and
 * it sees the layout fade out overhead, where the painting says nothing, since its sky band lies
 * within 2 deg of the painter's horizon. Both scale with the fray, 1 - painter amount: the painter
 * view keeps the calibrated layout and leaving it stays continuous. sky.frag mirrors this file.
 */
import { smoothstep } from '../core/math';
import type { Vec3 } from '../geo/types';
import { fbm3, STREAK_FBM_STD, STREAK_OCTAVES } from './skyNoise';

export const FREE_FRONT = {
  amplitudeDeg: 12,
  heightDeg: 4,
  seedOffset: 29,
  overheadFromDeg: 35,
  overheadToDeg: 65,
} as const;

const FRONT_NOISE_LIMIT = 0.5 / STREAK_FBM_STD;

export interface FrontField {
  readonly frontScale: Vec3;
  readonly frontAmplitude: number;
  /*
   * Beyond this azimuth from the painter yaw no displacement reaches the window, so the shader
   * skips the front noise there.
   */
  readonly frontReach: number;
  readonly overheadDeg: readonly [number, number];
  readonly seed: number;
}

export function frontReachDeg(windowExtentDeg: number): number {
  return windowExtentDeg + FRONT_NOISE_LIMIT * FREE_FRONT.amplitudeDeg;
}

export function frontNoise(field: FrontField, d: Vec3): number {
  const s = field.frontScale;
  const n = fbm3(
    d[0] * s[0],
    d[1] * s[1],
    d[2] * s[2],
    field.seed + FREE_FRONT.seedOffset,
    STREAK_OCTAVES
  );
  return (n - 0.5) / STREAK_FBM_STD;
}

export function windowAzimuth(
  field: FrontField,
  d: Vec3,
  azimuthFromYaw: number,
  fray: number
): number {
  if (fray <= 0 || Math.abs(azimuthFromYaw) >= field.frontReach) return azimuthFromYaw;
  return azimuthFromYaw + fray * field.frontAmplitude * frontNoise(field, d);
}

export function overheadWeight(field: FrontField, elevationDeg: number, fray: number): number {
  const [from, to] = field.overheadDeg;
  return 1 - fray * smoothstep(from, to, elevationDeg);
}
